// Position groups for sports
// Simple position groups for organization and display
// Coach-specific labels are handled at the formation level (formation_spots.label)

import type { Sport } from '../types';

/**
 * Position group (e.g., RB, WR, OL, DL, LB, DB)
 */
export interface PositionGroup {
  key: string; // Group identifier (e.g., 'RB', 'WR', 'OL', 'DL', 'LB', 'DB')
  name: string; // Display name (e.g., 'Running Back', 'Wide Receiver', 'Offensive Line')
}

/**
 * Unit structure (Offense, Defense, Special Teams, or Positions for single-unit sports)
 */
export type PositionUnitType = 'Offense' | 'Defense' | 'Special Teams' | 'Positions';

export interface PositionUnit {
  unit: PositionUnitType;
  groups: PositionGroup[];
}

/**
 * Sport position groups
 */
export interface SportPositionGroups {
  sport: Sport;
  units: PositionUnit[];
}

/**
 * Football position groups
 * Simple groups for organization - coach-specific labels handled in formations
 */
export const FOOTBALL_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Football',
  units: [
    {
      unit: 'Offense',
      groups: [
        { key: 'QB', name: 'Quarterback' },
        { key: 'RB', name: 'Running Back' },
        { key: 'WR', name: 'Wide Receiver' },
        { key: 'TE', name: 'Tight End' },
        { key: 'OL', name: 'Offensive Line' },
      ],
    },
    {
      unit: 'Defense',
      groups: [
        { key: 'DL', name: 'Defensive Line' },
        { key: 'LB', name: 'Linebacker' },
        { key: 'DB', name: 'Defensive Back' },
      ],
    },
    {
      unit: 'Special Teams',
      groups: [
        { key: 'K', name: 'Kicker' },
        { key: 'P', name: 'Punter' },
        { key: 'LS', name: 'Long Snapper' },
        { key: 'KR', name: 'Kick Returner' },
        { key: 'PR', name: 'Punt Returner' },
      ],
    },
  ],
};

/** Flag Football: same position groups as tackle football */
export const FLAG_FOOTBALL_POSITION_GROUPS: SportPositionGroups = {
  ...FOOTBALL_POSITION_GROUPS,
  sport: 'FlagFootball',
};

/** Basketball position groups */
export const BASKETBALL_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Basketball',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'PG', name: 'Point Guard' },
        { key: 'SG', name: 'Shooting Guard' },
        { key: 'SF', name: 'Small Forward' },
        { key: 'PF', name: 'Power Forward' },
        { key: 'C', name: 'Center' },
      ],
    },
  ],
};

/** Soccer position groups */
export const SOCCER_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Soccer',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'GK', name: 'Goalkeeper' },
        { key: 'LB', name: 'Left Back' },
        { key: 'CB', name: 'Center Back' },
        { key: 'RB', name: 'Right Back' },
        { key: 'CDM', name: 'Defensive Mid' },
        { key: 'CM', name: 'Center Mid' },
        { key: 'CAM', name: 'Attacking Mid' },
        { key: 'LW', name: 'Left Wing' },
        { key: 'RW', name: 'Right Wing' },
        { key: 'ST', name: 'Striker' },
      ],
    },
  ],
};

/** Baseball position groups */
export const BASEBALL_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Baseball',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'P', name: 'Pitcher' },
        { key: 'C', name: 'Catcher' },
        { key: '1B', name: 'First Base' },
        { key: '2B', name: 'Second Base' },
        { key: '3B', name: 'Third Base' },
        { key: 'SS', name: 'Shortstop' },
        { key: 'LF', name: 'Left Field' },
        { key: 'CF', name: 'Center Field' },
        { key: 'RF', name: 'Right Field' },
        { key: 'DH', name: 'Designated Hitter' },
      ],
    },
  ],
};

/** Softball position groups */
export const SOFTBALL_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Softball',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'P', name: 'Pitcher' },
        { key: 'C', name: 'Catcher' },
        { key: '1B', name: 'First Base' },
        { key: '2B', name: 'Second Base' },
        { key: '3B', name: 'Third Base' },
        { key: 'SS', name: 'Shortstop' },
        { key: 'LF', name: 'Left Field' },
        { key: 'CF', name: 'Center Field' },
        { key: 'RF', name: 'Right Field' },
        { key: 'DP', name: 'Designated Player' },
      ],
    },
  ],
};

/** Volleyball position groups */
export const VOLLEYBALL_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Volleyball',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'OH', name: 'Outside Hitter' },
        { key: 'MB', name: 'Middle Blocker' },
        { key: 'OPP', name: 'Opposite' },
        { key: 'S', name: 'Setter' },
        { key: 'L', name: 'Libero' },
        { key: 'DS', name: 'Defensive Specialist' },
      ],
    },
  ],
};

/** Hockey position groups */
export const HOCKEY_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Hockey',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'C', name: 'Center' },
        { key: 'LW', name: 'Left Wing' },
        { key: 'RW', name: 'Right Wing' },
        { key: 'LD', name: 'Left Defense' },
        { key: 'RD', name: 'Right Defense' },
        { key: 'G', name: 'Goalie' },
      ],
    },
  ],
};

/** Lacrosse position groups */
export const LACROSSE_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Lacrosse',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'A', name: 'Attack' },
        { key: 'M', name: 'Midfield' },
        { key: 'D', name: 'Defense' },
        { key: 'G', name: 'Goalie' },
        { key: 'LSM', name: 'Long Stick Mid' },
        { key: 'SSDM', name: 'Short Stick Defensive Mid' },
      ],
    },
  ],
};

/** Tennis position groups (singles/doubles ladder) */
export const TENNIS_POSITION_GROUPS: SportPositionGroups = {
  sport: 'Tennis',
  units: [
    {
      unit: 'Positions',
      groups: [
        { key: 'S1', name: 'Singles 1' },
        { key: 'S2', name: 'Singles 2' },
        { key: 'S3', name: 'Singles 3' },
        { key: 'S4', name: 'Singles 4' },
        { key: 'S5', name: 'Singles 5' },
        { key: 'S6', name: 'Singles 6' },
        { key: 'D1', name: 'Doubles 1' },
        { key: 'D2', name: 'Doubles 2' },
        { key: 'D3', name: 'Doubles 3' },
      ],
    },
  ],
};

/**
 * Get position groups for a sport
 */
export function getPositionGroupsForSport(sport: Sport): SportPositionGroups | null {
  switch (sport) {
    case 'Football':
      return FOOTBALL_POSITION_GROUPS;
    case 'FlagFootball':
      return FLAG_FOOTBALL_POSITION_GROUPS;
    case 'Basketball':
      return BASKETBALL_POSITION_GROUPS;
    case 'Soccer':
      return SOCCER_POSITION_GROUPS;
    case 'Baseball':
      return BASEBALL_POSITION_GROUPS;
    case 'Softball':
      return SOFTBALL_POSITION_GROUPS;
    case 'Volleyball':
      return VOLLEYBALL_POSITION_GROUPS;
    case 'Hockey':
      return HOCKEY_POSITION_GROUPS;
    case 'Lacrosse':
      return LACROSSE_POSITION_GROUPS;
    case 'Tennis':
      return TENNIS_POSITION_GROUPS;
    default:
      return null;
  }
}

/**
 * Get all position groups for a sport and unit
 */
export function getPositionGroups(
  sport: Sport,
  unit: PositionUnitType
): PositionGroup[] {
  const groups = getPositionGroupsForSport(sport);
  if (!groups) return [];

  const unitData = groups.units.find(u => u.unit === unit);
  return unitData?.groups || [];
}

/**
 * Get all position group keys for a sport
 */
export function getAllPositionGroupKeys(sport: Sport): string[] {
  const groups = getPositionGroupsForSport(sport);
  if (!groups) return [];

  return groups.units.flatMap(unit => unit.groups.map(g => g.key));
}

